/**
 * Settings → Modules: what this business uses.
 *
 * Each module shows the server's answer: whether the plan includes it, whether
 * it is on, and whether that is this kind of business's default or a switch
 * somebody set. Switching changes access only — turning a module off hides its
 * screens and refuses its requests, and never deletes a record; turning it
 * back on finds everything where it was left.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BUSINESS_TYPE_LABELS,
  SOCIAL_AI_CHANNELS,
  type BusinessType,
  type ModuleKey,
} from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { Badge, Button, Card, CardHeader, ErrorState, Spinner } from '../../components/ui';

interface ModuleRow {
  key: ModuleKey;
  label: string;
  description: string;
  granted: boolean;
  enabled: boolean;
  core: boolean;
  source: 'explicit' | 'default';
  typeDefault: boolean;
}

/**
 * Which modules have a screen of their own, and which tab it is.
 *
 * Deliberately a short list rather than a lookup on every module: most modules
 * are a capability rather than a thing to configure, and a Manage button that
 * led to the module list again would be worse than none.
 */
const MANAGED_AT: Partial<Record<string, string>> = {
  api_integrations: 'integrations',
};

export function ModulesSettings({ onManage }: { onManage: (tab: string) => void }): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const modules = useQuery({
    queryKey: ['modules', business?.businessId],
    queryFn: () => api.get<{ data: ModuleRow[] }>(path('/modules')),
    enabled: Boolean(business),
  });

  const toggle = useMutation({
    mutationFn: ({ key, enabled }: { key: ModuleKey; enabled: boolean }) =>
      api.patch(path(`/modules/${key}`), { enabled }),
    onSuccess: () => {
      setError(null);
      // The sidebar and every gated screen read this same query.
      void queryClient.invalidateQueries({ queryKey: ['modules'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not change this module.'),
  });

  if (modules.isPending) return <Spinner />;
  if (modules.isError) {
    return <ErrorState message="Could not load modules." onRetry={() => void modules.refetch()} />;
  }

  const typeLabel = business ? BUSINESS_TYPE_LABELS[business.businessType as BusinessType] : undefined;
  const editable = can('settings:update');

  return (
    <Card className="max-w-3xl">
      <CardHeader
        title="Modules"
        subtitle={`What this business uses. Defaults follow the business type${typeLabel ? ` (${typeLabel})` : ''}; switching one off hides it and keeps its records.`}
      />
      {error && (
        <p role="alert" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </p>
      )}
      <ul className="divide-y divide-slate-100">
        {modules.data.data.map((m) => (
          <li key={m.key} className="flex flex-wrap items-center gap-3 py-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-900">{m.label}</p>
              <p className="text-xs text-slate-500">{m.description}</p>
              <p className="mt-0.5 text-xs text-slate-400">
                {m.core
                  ? m.key === 'pharmacy'
                    ? 'Always on in a pharmacy'
                    : 'Always on'
                  : !m.granted
                    ? 'Not included in your plan'
                    : m.source === 'explicit'
                      ? 'Set in Settings'
                      : `Default for ${typeLabel ?? 'this business type'}`}
              </p>
              {/*
                Which channels Social Media AI actually speaks on.

                The feature is sold under one name and one switch, and WhatsApp
                is the only channel built — so the honest thing is to list the
                rest and say they are coming, rather than let a name that says
                "social media" imply five integrations that do not exist. The
                list and each status come from the shared catalogue, so turning
                one on is a one-word edit there and not a hunt through
                components for the word "soon".
              */}
              {m.key === 'whatsapp_ai' && (
                <ul className="mt-1.5 flex flex-wrap gap-1.5">
                  {SOCIAL_AI_CHANNELS.map((channel) => (
                    <li key={channel.key}>
                      <Badge tone={channel.status === 'live' ? 'success' : 'neutral'}>
                        {channel.label}
                        {channel.status === 'coming_soon' && ' · Coming soon'}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Badge tone={m.enabled ? 'success' : 'neutral'}>{m.enabled ? 'On' : 'Off'}</Badge>
            {/*
              A module row that only toggles is a dead end.

              "API & Integrations" switched On said nothing about where to go
              next, and the thing it governs — issuing a credential so another
              system can write here — lives one tab away with no sign that it
              does. Somebody looking for it reasonably concluded the row was
              broken rather than that it was a switch.

              Only for modules that actually lead somewhere, and only when the
              module is on: offering to manage something that is switched off
              would be the same dead end with an extra click.
            */}
            {MANAGED_AT[m.key] && m.enabled && (
              <Button size="sm" variant="secondary" onClick={() => onManage(MANAGED_AT[m.key]!)}>
                Manage
              </Button>
            )}
            {editable && !m.core && m.granted && (
              <Button
                size="sm"
                variant="secondary"
                loading={toggle.isPending && toggle.variables?.key === m.key}
                onClick={() => toggle.mutate({ key: m.key, enabled: !m.enabled })}
              >
                {m.enabled ? 'Switch off' : 'Switch on'}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
